import type { Itinerary, UiLang, Stop, TripForm } from "./types";

/**
 * Tar vare på den sist lagde reiseplanen, så den finnes uten dekning.
 *
 * Service workeren bufrer alt appen henter med GET — appen selv, bildene,
 * kartflisene. Men planen kommer fra en POST til /api/itinerary, og POST-svar
 * kan ikke bufres. Uten dette ville appen åpnet seg fint på Finse og vært helt
 * tom.
 *
 * Lagringen skjer i localStorage framfor IndexedDB fordi planen er liten
 * (titalls kilobyte) og fordi den skal leses synkront ved oppstart, før noe
 * tegnes.
 */

const KEY = "reise-planer:siste-plan";

/** Planen kastes etter en måned. Da er værvarselet i den uansett foreldet. */
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Formatet på det lagrede. Økes når `Itinerary` får felter appen regner med.
 *
 * localStorage overlever en utrulling, og en plan lagret av gårsdagens
 * versjon leses av dagens kode. Da `connections` og `producers` kom til, var
 * de påkrevde felter i typen og fraværende i alt som lå lagret — første
 * `day.connections.find(...)` på en gammel plan kaster, og appen viser en
 * hvit side til brukeren tømmer nettleserdataene sine.
 *
 * Alternativet var å gjøre hvert nytt felt valgfritt i frontend-typen og
 * strø `?.` utover. Det ville flyttet problemet inn i hver komponent og latt
 * typen lyve om hva serveren faktisk sender. Å kaste en foreldet plan koster
 * brukeren én henting neste gang de er på nett.
 */
const SCHEMA = 2;

interface Saved {
  schema?: number;
  savedAt: number;
  lang: UiLang;
  form: TripForm;
  itinerary: Itinerary;
  allStops: Stop[];
}

export function saveItinerary(
  form: TripForm,
  itinerary: Itinerary,
  allStops: Stop[],
  lang: UiLang,
): void {
  try {
    const payload: Saved = { schema: SCHEMA, savedAt: Date.now(), lang, form, itinerary, allStops };
    localStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    /*
     * Privat vindu, full disk eller blokkert lagring. Ingenting å gjøre — og
     * en feilmelding her ville vært verre enn stillhet, siden planen på
     * skjermen er helt i orden.
     */
  }
}

export function loadItinerary(): Saved | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;

    const saved = JSON.parse(raw) as Saved;
    if (!saved.itinerary || !saved.form) return null;

    // Lagret av en eldre utgave, og mangler felter appen nå regner med.
    if (saved.schema !== SCHEMA) {
      localStorage.removeItem(KEY);
      return null;
    }

    // En plan fra i fjor er ikke til hjelp; værvarselet i den er lenge utløpt.
    if (Date.now() - saved.savedAt > MAX_AGE_MS) {
      localStorage.removeItem(KEY);
      return null;
    }

    return saved;
  } catch {
    return null;
  }
}

/**
 * Henter bildene for rutas stopp, så de ligger i bufferen før man mister
 * dekningen.
 *
 * Service workeren bufrer bare det som faktisk blir bedt om, og et bilde
 * hentes først når man holder musa over stoppet. Uten dette ville planen
 * vært der på Finse, men uten et eneste bilde — og det er nettopp da man
 * har tid til å se på dem.
 *
 * Kallene er «fire and forget»: de skal ikke forsinke noe, og en som feiler
 * betyr bare ett bilde mindre.
 */
export function prefetchImages(stops: Stop[]): void {
  const seen = new Set<string>();

  for (const stop of stops) {
    const src = stop.image?.src;
    if (!src || seen.has(src)) continue;
    seen.add(src);

    // `no-cors` er unødvendig her — bildene ligger på samme opprinnelse.
    fetch(src).catch(() => undefined);
  }
}

/**
 * Registrerer service workeren.
 *
 * Bare i produksjon: i utvikling ville den servert bufret kode og skjult
 * endringer man nettopp gjorde, som er en særlig irriterende feilkilde.
 */
export function registerServiceWorker(): void {
  if (!("serviceWorker" in navigator) || !import.meta.env.PROD) return;

  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch((error) => {
      // Uten service worker virker appen som før, bare uten offline-støtte.
      console.warn("Klarte ikke registrere service worker:", error);
    });
  });
}
